import type { ApiResponse } from '../../types'
import type { CompleteAccountRequest, MeResponse, ParentVerification } from '../../types/community'
import { apiError, client, CotApiError, unwrap } from './client'

export async function getVerification(): Promise<ParentVerification> {
  try {
    return unwrap((await client.get<ApiResponse<ParentVerification>>('/consents/verification')).data)
  } catch (error) { throw apiError(error) }
}

async function providerSession(kind: 'checkout' | 'portal'): Promise<{ url: string }> {
  try {
    return unwrap((await client.post<ApiResponse<{ url: string }>>(`/consents/verification/${kind}`)).data)
  } catch (error) {
    const normalized = apiError(error)
    if (normalized.message === 'verification_unavailable') {
      throw Object.assign(new CotApiError(
        'Payment verification is temporarily unavailable. Your account is saved. Please check again later.',
        normalized.status,
      ), { code: normalized.message, cause: normalized })
    }
    throw normalized
  }
}

export const startVerificationCheckout = () => providerSession('checkout')
export const createVerificationPortal = () => providerSession('portal')

export async function withdrawVerification(): Promise<ParentVerification> {
  try {
    return unwrap((await client.post<ApiResponse<ParentVerification>>('/consents/verification/withdraw')).data)
  } catch (error) { throw apiError(error) }
}

export async function completeAccount(request: CompleteAccountRequest): Promise<MeResponse> {
  try {
    return unwrap((await client.post<ApiResponse<MeResponse>>('/account/complete', request)).data)
  } catch (error) { throw apiError(error) }
}

/** Only hosted Stripe pages returned by our authenticated API may receive a browser redirect. */
export function openVerificationProvider(url: string): void {
  const target = new URL(url)
  if (target.protocol !== 'https:' || !['checkout.stripe.com', 'billing.stripe.com'].includes(target.hostname)) {
    throw new Error('The payment link could not be verified. Please try again.')
  }
  window.location.assign(target.href)
}
